import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMessage } from "../lib/api";
import { t, formatDate } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { MediaTile, EmptyState, Loader } from "../components/Blocks";

const TABS = [
  { id: "profile", label: "Mon profil" },
  { id: "participations", label: "Mes participations" },
  { id: "media", label: "Mes médias" },
  { id: "challenges", label: "Mes challenges" },
  { id: "consents", label: "Mes consentements" },
];

export default function Account() {
  const { user } = useAuth();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [participations, setParticipations] = useState([]);
  const [media, setMedia] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [consents, setConsents] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const [p, pa, m, c, co] = await Promise.all([
      api.get("/account/profile"),
      api.get("/account/participations"),
      api.get("/account/media"),
      api.get("/account/challenges"),
      api.get("/account/consents"),
    ]);
    setProfile(p.data);
    setParticipations(pa.data);
    setMedia(m.data);
    setChallenges(c.data);
    setConsents(co.data);
  };

  useEffect(() => {
    loadAll().catch((e) => toast.error(errMessage(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/account/profile", {
        display_name: profile.display_name,
        city: profile.city,
        bio: profile.bio,
        portfolio_url: profile.portfolio_url,
        profile_photo_url: profile.profile_photo_url,
      });
      setProfile(data);
      toast.success("Profil mis à jour");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/account/consents/${id}/revoke`);
      setConsents(await api.get("/account/consents").then((r) => r.data));
      toast.success("Consentement révoqué");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (!profile) return <Loader />;

  return (
    <div data-testid="account-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
      <Seo title="Mon compte" description="Espace membre VytaliBoots." path="/compte" noindex />
      <div className="vb-label mb-3">Espace membre</div>
      <h1 className="vb-display text-4xl lg:text-5xl font-black">{profile.display_name}</h1>
      <p className="mt-3 text-sm text-neutral-500">
        {user?.email} · rôle système : {user?.security_role} · profils :{" "}
        {(profile.profile_types || []).map((p) => t(`profile_types.${p}`, p)).join(", ")}
      </p>

      <div className="flex flex-wrap gap-3 mt-10 mb-10" data-testid="account-tabs">
        {TABS.map((tb) => (
          <button key={tb.id} className="vb-chip" data-active={tab === tb.id} data-testid={`account-tab-${tb.id}`} onClick={() => setTab(tb.id)}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <form onSubmit={save} className="max-w-2xl space-y-8" data-testid="account-profile-form">
          <div>
            <label className="vb-label">Nom affiché</label>
            <input className="vb-input" value={profile.display_name || ""} onChange={(e) => setProfile({ ...profile, display_name: e.target.value })} data-testid="account-display-name" />
          </div>
          <div>
            <label className="vb-label">Ville</label>
            <input className="vb-input" value={profile.city || ""} onChange={(e) => setProfile({ ...profile, city: e.target.value })} data-testid="account-city" />
          </div>
          <div>
            <label className="vb-label">Bio</label>
            <textarea className="vb-input min-h-[120px]" value={profile.bio || ""} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} data-testid="account-bio" />
          </div>
          <div>
            <label className="vb-label">Photo de profil (URL)</label>
            <input className="vb-input" value={profile.profile_photo_url || ""} onChange={(e) => setProfile({ ...profile, profile_photo_url: e.target.value })} data-testid="account-photo" />
          </div>
          <button type="submit" disabled={saving} className="vb-btn vb-btn-primary" data-testid="account-save">
            {saving ? "…" : "Enregistrer"}
          </button>
        </form>
      )}

      {tab === "participations" && (
        participations.length === 0 ? (
          <EmptyState message="Aucune participation enregistrée." testid="account-participations-empty" />
        ) : (
          <div className="space-y-4" data-testid="account-participations">
            {participations.map((e) => (
              <div key={e.id} className="vb-card p-6 flex flex-wrap justify-between gap-4">
                <div>
                  <div className="vb-display text-lg">{e.name}</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {e.event_type} · {formatDate(e.date_time)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "media" && (
        media.length === 0 ? (
          <EmptyState message="Aucun média associé à votre profil." testid="account-media-empty" />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4" data-testid="account-media">
            {media.map((m, i) => (
              <MediaTile key={m.id} item={m} testid={`account-media-${i}`} className="aspect-square" />
            ))}
          </div>
        )
      )}

      {tab === "challenges" && (
        challenges.length === 0 ? (
          <EmptyState message="Aucune participation à un challenge." testid="account-challenges-empty" />
        ) : (
          <div className="space-y-4" data-testid="account-challenges">
            {challenges.map((e) => (
              <div key={e.id} className="vb-card p-6">
                <div className="vb-display text-lg">{e.challenge?.title || "Challenge"}</div>
                <div className="text-xs text-neutral-500 mt-1">Statut : {t(`statuses.${e.status}`, e.status)}</div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "consents" && (
        consents.length === 0 ? (
          <EmptyState message="Aucun consentement enregistré." testid="account-consents-empty" />
        ) : (
          <div className="space-y-4" data-testid="account-consents">
            {consents.map((c) => (
              <div key={c.id} className="vb-card p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-sm">{c.consent_type}</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    v{c.consent_version} · {formatDate(c.accepted_at)} · usages :{" "}
                    {(c.allowed_usage || []).join(", ") || "—"}
                  </div>
                </div>
                {c.revoked ? (
                  <span className="text-xs text-[#FF3B30]">Révoqué</span>
                ) : (
                  <button onClick={() => revoke(c.id)} className="vb-btn vb-btn-ghost !py-2" data-testid={`account-revoke-${c.id}`}>
                    Révoquer
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
